import type { UserType } from '@econtractor/types';
import { DateTime as DT } from 'luxon';
import { TaskTypeStatus } from '@econtractor/types';
import { safeJSONParse } from 'utils/helpers/safeParsing';
import { getPresetPeriodRange, periodRangeToMillis, type ReportPeriodType } from '../ReportDate/periodUtils';
import type { ReportDateFilterState } from './reportObjectsStorage';

const STORAGE_KEY = 'reportTasks.filters';

const ALLOWED_STATUSES = new Set<number>([
    TaskTypeStatus.NEW,
    TaskTypeStatus.INWORK,
    TaskTypeStatus.COMPLETED,
    TaskTypeStatus.ACCEPTED,
]);

export type ReportTasksViewMode = 'summary' | 'detail';

export interface ReportTasksFiltersState extends ReportDateFilterState {
    executor: UserType | null;
    viewMode: ReportTasksViewMode;
    statuses: number[];
    showZeroTasks: boolean;
}

const parseStatuses = (value: unknown): number[] => {
    if (!Array.isArray(value)) return [TaskTypeStatus.ACCEPTED];
    return value.map((item) => Number(item)).filter((item) => ALLOWED_STATUSES.has(item));
};

const createDefaultFilters = (): ReportTasksFiltersState => {
    const { start, end } = getPresetPeriodRange('month', DT.now());
    return {
        period: 'month',
        dates: periodRangeToMillis(start, end),
        customRange: null,
        executor: null,
        viewMode: 'summary',
        statuses: [TaskTypeStatus.ACCEPTED],
        showZeroTasks: false,
    };
};

export const loadReportTasksFilters = (): ReportTasksFiltersState => {
    const saved = safeJSONParse(sessionStorage.getItem(STORAGE_KEY)) as Partial<ReportTasksFiltersState> | null;
    if (!saved?.dates?.[0] || !saved?.dates?.[1]) {
        return createDefaultFilters();
    }

    const period: ReportPeriodType =
        saved.period === 'month' || saved.period === 'quarter' || saved.period === 'year' || saved.period === 'custom'
            ? saved.period
            : 'month';

    return {
        period,
        dates: [saved.dates[0], saved.dates[1]],
        customRange:
            saved.customRange?.[0] && saved.customRange?.[1]
                ? [saved.customRange[0], saved.customRange[1]]
                : null,
        executor: saved.executor?.ID ? saved.executor : null,
        viewMode: saved.viewMode === 'detail' ? 'detail' : 'summary',
        statuses: parseStatuses(saved.statuses),
        showZeroTasks: saved.showZeroTasks === true,
    };
};

export const saveReportTasksFilters = (state: ReportTasksFiltersState): void => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

export { createDefaultFilters as createDefaultReportTasksFilters };
